/**
 * PM-D38 — AI-models edit panel on the project detail page (projects/v1@2.10.0).
 *
 * The post-create edit path for the per-project AI-model selection. Loads the
 * project's CURRENT `aiModelIds` (from the single-project detail GET, which now
 * exists as a real endpoint), pre-selects the reused single `AiModelMultiSelect`,
 * and on Save fires `PUT /projects/{projectId}/ai-models` (`replaceProjectAiModels`)
 * with the FULL replacement list. The BE re-derives the deprecated booleans from
 * `aiModelIds.length > 0`.
 *
 * Owner-only: the panel renders for the owner project's project-management role —
 * the same gate the page applies to the Sharing & access panel (the BE re-asserts
 * it with the same permission matrix, so it is the security boundary regardless of
 * the UI gate). A member project's models are managed by its owning company.
 *
 * Prefill note: the project-detail read sources the current selection. After a
 * Save the PUT response (full `ProjectDetail`) is written into the same cache key,
 * so the panel re-prefills with the canonical selection without a refetch.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useToast } from "../../account/ToastContext.js";
import { useTenantSettingsQuery } from "../../tenancy/hooks.js";
import { AiModelMultiSelect } from "../AiModelMultiSelect.js";
import {
  useProjectDetailQuery,
  useReplaceProjectAiModelsMutation,
} from "../useProjects.js";

export interface AiModelsPanelProps {
  projectId: string;
  /** True when the project is the caller's OWN. A member project's AI-model
   *  selection is managed by its owning company. Defaults true. */
  isOwnerProject?: boolean;
}

/** Stable set-equality for two id lists (order-insensitive) — drives the dirty
 *  check so Save is disabled until the selection actually changes. */
function sameIds(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

export function AiModelsPanel({
  projectId,
  isOwnerProject = true,
}: AiModelsPanelProps): ReactElement {
  const { toast } = useToast();
  const detail = useProjectDetailQuery(isOwnerProject ? projectId : null);
  const replace = useReplaceProjectAiModelsMutation(projectId);
  // PM-D39 — the per-tenant cap on models-per-project (null = unlimited). The
  // BE 422s over-limit; this caps the selector as a UX guard.
  const settings = useTenantSettingsQuery();
  const maxModels = settings.data?.maxAiModelsPerProject ?? null;

  // The persisted selection (the prefill baseline). Empty when none / not yet
  // loaded.
  const persisted = useMemo<string[]>(
    () => detail.data?.aiModelIds ?? [],
    [detail.data?.aiModelIds],
  );

  // Local editable selection. Seeded from the persisted baseline and re-synced
  // whenever the baseline changes (initial load + after a successful Save, which
  // updates the detail cache).
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedIds(persisted);
  }, [persisted]);

  const dirty = !sameIds(selectedIds, persisted);

  function onSave(): void {
    // FULL-REPLACE the project's selection with the flat id list.
    replace.mutate([...selectedIds], {
      onSuccess: () => {
        toast("AI models updated.", "success");
      },
      onError: () => {
        toast("Couldn't update AI models — try again.", "error");
      },
    });
  }

  if (!isOwnerProject) {
    return (
      <Alert severity="info" variant="outlined">
        AI models for a shared project are managed by its owning company.
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        AI models
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose which AI models this project runs on its datasets. Saving
        replaces the project&apos;s entire selection.
      </Typography>

      {detail.isPending ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      ) : detail.isError ? (
        <Alert severity="error">
          Couldn&apos;t load the project&apos;s AI models.
        </Alert>
      ) : (
        <Stack spacing={2}>
          <AiModelMultiSelect
            value={selectedIds}
            onChange={setSelectedIds}
            maxSelections={maxModels}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={onSave}
              disabled={!dirty || replace.isPending}
            >
              {replace.isPending ? "Saving…" : "Save AI models"}
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
}
